/**
 * /refund — Refund Policy.
 * Operator: Thoughtplanet Advisory Services LLP.
 *
 * Curvle Pro is a one-time payment for lifetime access — NOT a subscription.
 * Aligned with Gumroad (our Merchant of Record) policy as published at
 * gumroad.com/help (refund articles 51, 190, 335): sellers set their own
 * policy; Gumroad processes refunds on the seller's behalf; Gumroad reserves
 * the right to issue refunds within 90 days at its discretion to prevent
 * chargebacks; duplicate/fraudulent charges are always refundable. We offer a
 * clear 7-day self-serve money-back window (the Gumroad-recommended "7-day
 * money-back guarantee" shape) and avoid a blanket "no refunds" stance, which
 * still permits chargebacks and can risk account suspension.
 */
import { LegalShell } from "@/components/LegalShell";

export function RefundPage() {
  return (
    <LegalShell title="Refund Policy" effectiveDate="June 10, 2026">
      <p>
        We want you to be happy with Curvle Pro. This Refund Policy explains
        when refunds are available and how to request one. Curvle Pro is sold
        through <strong>Gumroad</strong>, which acts as our Merchant of Record
        and processes refunds on our behalf.
      </p>

      <h2>1. What You&rsquo;re Buying</h2>
      <p>
        Curvle Pro is a <strong>one-time payment for lifetime access</strong>.
        You pay once and keep access to Pro features for the lifetime of the
        Service. There is no recurring charge, no auto-renewal, and nothing to
        cancel. The base game (one daily puzzle, leaderboard, streaks) is and
        remains free.
      </p>

      <h2>2. 7-Day Money-Back Guarantee (Self-Serve)</h2>
      <p>
        You can request a full refund within <strong>7 days</strong> of your
        purchase, for any reason, provided that abuse of the Service has not
        occurred during that period. To qualify:
      </p>
      <ul>
        <li>The request must be made within 7 days of the original payment
          date.</li>
        <li>The account must not have been used in violation of our{" "}
          <a href="/terms">Terms of Service</a>.</li>
      </ul>
      <p>
        Once approved, we refund the full amount through the original payment
        method via Gumroad. The refund typically posts within 5&ndash;10
        business days, depending on your bank. Your Pro access ends immediately
        on refund.
      </p>

      <h2>3. Beyond 7 Days</h2>
      <p>
        After 7 days, refunds are not guaranteed and routine change-of-mind
        requests are not eligible. However, in line with Gumroad&rsquo;s
        policy, the following are always addressed:
      </p>
      <ul>
        <li>
          <strong>Duplicate or fraudulent charges</strong> are refunded
          whenever identified, at any time.
        </li>
        <li>
          <strong>A verifiable bug or prolonged outage</strong> that prevented
          you from using Pro features will be reviewed on request, and a full
          or partial refund issued where warranted.
        </li>
      </ul>
      <p>
        If you have contacted us about a refund and have not heard back within
        30 days, Gumroad allows you to escalate the request to Gumroad Support
        directly (with proof of your correspondence); Gumroad will then follow
        up with us on your behalf.
      </p>

      <h2>4. Gumroad as Merchant of Record</h2>
      <p>
        Gumroad, Inc. is the seller of record for your purchase and processes
        payments and refunds on our behalf. The policy above is the policy we
        apply, and Gumroad attaches it to any card or PayPal dispute as
        evidence. Independently of the windows above,{" "}
        <strong>
          Gumroad reserves the right to issue a refund within 90 days of
          purchase at its own discretion to prevent chargebacks
        </strong>
        . Approved refunds are executed by Gumroad to your original payment
        method.
      </p>

      <h2>5. How to Request a Refund</h2>

      <h3>5.1 Self-serve (within the 7-day window)</h3>
      <p>
        For most refund requests within the first 7 days, you can issue the
        refund yourself in two clicks:
      </p>
      <ul>
        <li>Open the <strong>Curvle Pro</strong> dialog (from the upgrade link
          on any page or your account menu).</li>
        <li>Click <strong>&ldquo;Request refund&rdquo;</strong>.</li>
        <li>Confirm in the dialog. Your Pro access ends immediately and the
          refund is issued through Gumroad to your original payment method.</li>
      </ul>
      <p>
        The refund typically posts within 5&ndash;10 business days. You&rsquo;ll
        receive an email confirmation from Gumroad when the refund is
        processed.
      </p>

      <h3>5.2 By email (after 7 days, or for any case the in-app flow doesn&rsquo;t cover)</h3>
      <p>
        For duplicate or fraudulent charges, a bug or outage (Section 3), or
        anything the self-serve flow doesn&rsquo;t cover, email{" "}
        <a href="mailto:vbraju@thoughtplanet.com">vbraju@thoughtplanet.com</a>{" "}
        from the email address on your Curvle account, with:
      </p>
      <ul>
        <li>Subject: <em>&ldquo;Curvle Pro refund request&rdquo;</em></li>
        <li>Approximate date of the original payment (or your Gumroad order
          number).</li>
        <li>A short note on the reason.</li>
      </ul>
      <p>
        You can also reply directly to your Gumroad receipt email to reach us.
        We aim to respond within <strong>3 business days</strong>.
      </p>

      <h2>6. Chargebacks</h2>
      <p>
        If you have a concern about a charge, please contact us before
        initiating a chargeback with your card issuer &mdash; direct contact is
        almost always the fastest way to resolve an issue, and we can issue a
        full or partial refund right away. Chargebacks initiated without prior
        contact may result in termination of the associated account in addition
        to any other remedy permitted by law.
      </p>

      <h2>7. Contact</h2>
      <p>
        Refund-related questions:{" "}
        <a href="mailto:vbraju@thoughtplanet.com">vbraju@thoughtplanet.com</a>
      </p>
    </LegalShell>
  );
}
